import { create } from 'zustand';

export interface MessageToast {
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  messageCount: number;
}

const MAX_TOASTS = 5;

interface ChatWidgetState {
  isTrayMinimized: boolean;
  activeConversationId: string | null;
  isChatMinimized: boolean;
  isNewMessageOpen: boolean;
  messageToasts: MessageToast[];

  openTray: () => void;
  minimizeTray: () => void;
  toggleTray: () => void;
  openChat: (conversationId: string) => void;
  closeChat: () => void;
  minimizeChat: () => void;
  restoreChat: () => void;
  openNewMessage: () => void;
  closeNewMessage: () => void;
  addMessageToast: (toast: Omit<MessageToast, 'messageCount'>) => void;
  dismissToast: (conversationId: string) => void;
}

export const useChatWidgetStore = create<ChatWidgetState>()((set) => ({
  isTrayMinimized: true,
  activeConversationId: null,
  isChatMinimized: false,
  isNewMessageOpen: false,
  messageToasts: [],

  openTray: () => set({ isTrayMinimized: false }),

  minimizeTray: () => set({ isTrayMinimized: true }),

  toggleTray: () =>
    set((state) => ({ isTrayMinimized: !state.isTrayMinimized })),

  openChat: (conversationId: string) => {
    // Blur tray search input so StickyChatWindow textarea can grab focus
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    set((state) => ({
      activeConversationId: conversationId,
      isChatMinimized: false,
      isNewMessageOpen: false,
      messageToasts: state.messageToasts.filter(
        (t) => t.conversationId !== conversationId
      ),
    }));
  },

  closeChat: () =>
    set({
      activeConversationId: null,
      isChatMinimized: false,
      isTrayMinimized: false,
    }),

  minimizeChat: () => set({ isChatMinimized: true }),

  restoreChat: () => set({ isChatMinimized: false }),

  openNewMessage: () => set({ isNewMessageOpen: true }),

  closeNewMessage: () => set({ isNewMessageOpen: false }),

  addMessageToast: (toast: Omit<MessageToast, 'messageCount'>) =>
    set((state) => {
      // Upsert by conversationId — increment count if already exists
      const existing = state.messageToasts.find(
        (t) => t.conversationId === toast.conversationId
      );
      if (existing) {
        return {
          messageToasts: state.messageToasts.map((t) =>
            t.conversationId === toast.conversationId
              ? { ...toast, messageCount: t.messageCount + 1 }
              : t
          ),
        };
      }
      // Cap at MAX_TOASTS — drop oldest (first) if at limit
      const next = [...state.messageToasts, { ...toast, messageCount: 1 }];
      return {
        messageToasts: next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next,
      };
    }),

  dismissToast: (conversationId: string) =>
    set((state) => ({
      messageToasts: state.messageToasts.filter(
        (t) => t.conversationId !== conversationId
      ),
    })),
}));
